import { inject, Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { AddArticleService } from '@configurator/project/add-article.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { lastValueFrom } from 'rxjs';
import { SuitableConnectionAreasVO } from '@project/components/article/model/vo/suitable-connection-areas.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';

@Injectable({
  providedIn: 'root'
})
export class PeContactService {

  private readonly containerService = inject(ContainerService);
  private readonly articleDataService = inject(ArticleDataService);
  private readonly addArticleService = inject(AddArticleService);

  private readonly moduleAccessoryArticleNosByBaseArticleNo: Record<string, string> = {
    '274-4201': '1317-274',
    '274-4221': '1317-274',
    '274-2401': '1217-274',
    '274-2421': '1217-274',
  };

  public async addPeAccessoryToArticles(articleUsageIds: string[]): Promise<void> {
    // @ts-ignore We filter out undefined values in the next line, so we can ignore the type error here.
    const articleContainers: ArticleContainer[] = articleUsageIds.map((articleUsageId) => this.containerService.findArticleContainerInGlobal(articleUsageId))
      .filter((container): boolean => container != undefined);

    const accessoryArticleNos: string[] = [];
    for (const articleContainer of articleContainers) {
      accessoryArticleNos.push(this.moduleAccessoryArticleNosByBaseArticleNo[articleContainer.article.articleNo]);
    }
    const accessories = await lastValueFrom(this.articleDataService.getLatestArticlesByNoObservable(accessoryArticleNos));

    for (let i = 0; i < articleContainers.length; i++) {
      const frontConnection = this.findFrontAcceptingConnection(accessories[i], articleContainers[i]);
      if (!frontConnection) {
        continue;
      }
      const suitableConnectionAreas = SuitableConnectionAreasVO.create(accessories[i], ArticleUtils.givingAreas(accessories[i]));
      await this.addArticleService.addArticleToConnections(
        suitableConnectionAreas,
        [frontConnection],
      );
    }
  }

  /**
   * A module can offer several connections for the same accessory. Only the front one must be used.
   * The front of an article faces the negative z-axis of the 3d scene.
   */
  private findFrontAcceptingConnection(accessoryArticle: ArticleVO, articleContainer: ArticleContainer): Connection | undefined {
    const suitableConnections = ConnectionInfoUtils.findSuitableAcceptingConnections(accessoryArticle, [ articleContainer ]);
    return suitableConnections.slice(1).reduce((frontConnection, candidate) =>
        this.getConnectionDepth(candidate) < this.getConnectionDepth(frontConnection) ? candidate : frontConnection,
      suitableConnections[0],
    );
  }

  private getConnectionDepth(connection: Connection): number {
    return MathUtils.calculateMiddlePointByRectangle(connection.globalRectangle).z;
  }
}
